import React, { useState, useEffect, useMemo } from "react";
import { useMsal } from "@azure/msal-react";
import { useTranslation } from "react-i18next"; 
import { IconButton, Spinner } from "@fluentui/react";
import { FiEdit2, FiFilter, FiRotateCcw } from "react-icons/fi";
import { getAllPermissions } from "../../Services/HeedifyServices";
import { useSignalRConnection } from "../../Hooks/useSignalRConnection";
import { SIGNALR_PATH } from "../../Config/Config";
import "./CSS/RealtimeDashboard.css";
import FilterComponent from "./FilterComponent";
import QueuesContainer from "../../Containers/RealtimeDashboard/QueuesContainer";

const RealtimeDashboard = (props) => {
  const authContext = useMsal();
  const { t } = useTranslation(); 
  const accounts = authContext.accounts;
  const activeAccount = accounts[0];
  const tenantId = activeAccount?.tenantId;
  const userId = activeAccount?.localAccountId;

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedScopes, setSelectedScopes] = useState(["All"]);
  const [selectedQueues, setSelectedQueues] = useState([]);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isFilterVisible, setIsFilterVisible] = useState(false);
  const [onSaveLayoutCallback, setOnSaveLayoutCallback] = useState(null);
  const [onResetLayoutCallback, setOnResetLayoutCallback] = useState(null);
  const [isLayoutModified, setIsLayoutModified] = useState(false);

  // Connexion SignalR centralisée
  const { connection, isConnected } = useSignalRConnection(SIGNALR_PATH, tenantId);

  // STEP 1: Récupérer tous les scopes depuis le store
  const allScopes = useMemo(() => {
    return props.scopes || {};
  }, [props.scopes]);

  // STEP 2: Récupérer toutes les queues depuis le store
  const allQueues = useMemo(() => {
    return props.queues || [];
  }, [props.queues]);

  // STEP 3: Convertir les queues en array si c'est un objet
  const queuesArray = useMemo(() => {
    if (Array.isArray(allQueues)) {
      return allQueues;
    }
    // Si c'est un objet, convertir en array
    return Object.values(allQueues);
  }, [allQueues]);

  // STEP 4: Tous les scopes sont accessibles (pas de filtre permission)
  const accessibleQueues = useMemo(() => {
    return queuesArray;
  }, [queuesArray]);

  // STEP 5: Extraire tous les scope IDs disponibles
  const availableScopes = useMemo(() => {
    const scopeKeys = Object.keys(allScopes);
    return ["All", ...scopeKeys];
  }, [allScopes]);

  // Grouper les queues par scope
  const queuesByScope = useMemo(() => {
    const grouped = { All: accessibleQueues };
    accessibleQueues.forEach(queue => {
      const scopeKey = queue.scope;
      if (!grouped[scopeKey]) {
        grouped[scopeKey] = [];
      }
      grouped[scopeKey].push(queue);
    });
    return grouped;
  }, [accessibleQueues]);

  // Initialiser toutes les queues au chargement
  useEffect(() => {
    if (accessibleQueues.length > 0 && selectedQueues.length === 0) {
      setSelectedQueues(accessibleQueues);
    }
  }, [accessibleQueues]);

  // Handler: Quand scopes changent (multiselect)
  const handleScopesChange = (scopes) => {
    setSelectedScopes(scopes);
    
    // Récupérer les queues des scopes sélectionnés
    let queuesInScopes = [];
    if (scopes.includes("All")) {
      // Si "All" est sélectionné, prendre toutes les queues
      queuesInScopes = accessibleQueues;
    } else {
      // Sinon, prendre les queues des scopes sélectionnés
      queuesInScopes = scopes.flatMap(scope => queuesByScope[scope] || []);
    }
    
    setSelectedQueues(queuesInScopes);
  };

  // Handler: Mise à jour des queues sélectionnées (reçoit les objets complets)
  const handleQueuesChange = (queueObjects) => {
    setSelectedQueues(queueObjects);
  };

  
 
  
  if (!activeAccount) {
    return <div className="realtime-dashboard-loading"><Spinner/></div>;
  }

  if (error) {
    return <div className="realtime-dashboard-error">{error}</div>;
  }
  
  return (
    <div className="realtime-dashboard-container">
      {/* Buttons Bar - Always Visible */}
      <div className="dashboard-buttons-bar">
        <IconButton
          title={isEditMode ? t('REALTIME.SAVE_LAYOUT') : t('REALTIME.EDIT_LAYOUT')}
          ariaLabel={isEditMode ? t('REALTIME.SAVE_LAYOUT') : t('REALTIME.EDIT_LAYOUT')}
          onClick={() => {
            if (isEditMode && onSaveLayoutCallback) {
              // Sauvegarder avant de désactiver le mode édition
              onSaveLayoutCallback();
              setIsLayoutModified(false);
            }
            setIsEditMode(!isEditMode);
          }}
          styles={{
            root: {
              width: 40,
              height: 40,
              backgroundColor: isEditMode ? 'var(--heedify-success)' : 'var(--heedify-primary)',
              border: 'none'
            },
            rootHovered: {
              backgroundColor: isEditMode ? 'var(--heedify-success)' : 'var(--heedify-primary-hover)'
            }
          }}
        >
          <FiEdit2 style={{ fontSize: 16, color: 'var(--heedify-white)' }} />
        </IconButton>
        {isEditMode && isLayoutModified && (
          <IconButton
            title={t('REALTIME.RESET_LAYOUT')}
            ariaLabel={t('REALTIME.RESET_LAYOUT')}
            onClick={() => {
              if (onResetLayoutCallback) {
                onResetLayoutCallback();
                setIsLayoutModified(false);
              }
            }}
            styles={{
              root: {
                width: 40,
                height: 40,
                backgroundColor: 'var(--heedify-text-disabled)',
                border: 'none'
              },
              rootHovered: {
                backgroundColor: 'var(--heedify-text-secondary)'
              }
            }}
          >
            <FiRotateCcw style={{ fontSize: 16, color: 'var(--heedify-white)' }} />
          </IconButton>
        )}
        <IconButton
          title={isFilterVisible ? t('REALTIME.HIDE_FILTERS') : t('REALTIME.SHOW_FILTERS')}
          ariaLabel={isFilterVisible ? t('REALTIME.HIDE_FILTERS') : t('REALTIME.SHOW_FILTERS')}
          onClick={() => setIsFilterVisible(!isFilterVisible)}
          styles={{
            root: {
              width: 40,
              height: 40,
              backgroundColor: isFilterVisible ? 'var(--heedify-primary-hover)' : 'var(--heedify-primary)',
              border: 'none'
            },
            rootHovered: {
              backgroundColor: 'var(--heedify-primary-hover)'
            }
          }}
        >
          <FiFilter style={{ fontSize: 16, color: 'var(--heedify-white)' }} />
        </IconButton>
      </div>

      {/* Filter Component - Conditionally Visible */}
      {isFilterVisible && (
        <FilterComponent
          availableScopes={availableScopes}
          queuesByScope={queuesByScope}
          selectedScopes={selectedScopes}
          selectedQueues={selectedQueues}
          onScopesChange={handleScopesChange}
          onQueuesChange={handleQueuesChange}
        />
      )}

      {/* Queues Component - Fusionne KPI et affiche les cartes + agents */}
      <QueuesContainer
        selectedQueues={selectedQueues}
        agents={props.agents}
        connection={connection}
        isConnected={isConnected}
        isEditMode={isEditMode}
        setIsEditMode={setIsEditMode}
        setSaveLayoutCallback={setOnSaveLayoutCallback}
        setResetLayoutCallback={setOnResetLayoutCallback}
        setIsLayoutModified={setIsLayoutModified}
      />
    </div>
  );
};

export default RealtimeDashboard;